import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { decrypt } from "../../utils/others";
import { getRole } from "../../utils/auth";

// Base URL of the cy-plugins deployment (must end with "/"). Same env the chat
// page loader (CyChatPlugin) uses.
const CY_PLUGINS_URL =
  process.env.REACT_APP_CY_PLUGINS_URL || "http://localhost:3050/";

const ChatUnreadContext = createContext({ unreadCount: 0 });

export const useChatUnread = () => useContext(ChatUnreadContext);

/**
 * Keeps the mentor's total unread chat count live — without opening the chat
 * page — so the nav chat icon can show a badge everywhere.
 *
 * It mounts a hidden iframe pointing at the cy-plugins bundle's `#/native`
 * handoff route, hands it the logged-in mentor's tokens via postMessage (the
 * same contract the StudentApp WebView uses), and the plugin redirects itself to
 * the headless `#/unread` route. That route connects to Stream and posts
 * `{ type: "chatUnread", count }` back to this window on every change.
 *
 * Mentor-only (chat is a mentor feature) and a no-op if there is no token.
 */
export function ChatUnreadProvider({ children }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (getRole() !== "mentor") return undefined;

    let user = {};
    try {
      user = decrypt(localStorage.getItem("user")) || {};
    } catch (e) {
      console.error("[ChatUnread] failed to read user", e);
    }
    if (!user.ACCESS_TOKEN) return undefined;

    const onMessage = (event) => {
      let data = event.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      if (data && data.type === "chatUnread" && typeof data.count === "number") {
        setUnreadCount(data.count);
      }
    };
    window.addEventListener("message", onMessage);

    // Hidden iframe: load #/native (token handoff) → plugin redirects to #/unread.
    const iframe = document.createElement("iframe");
    iframe.title = "cy-chat-unread";
    iframe.setAttribute("aria-hidden", "true");
    iframe.src = `${CY_PLUGINS_URL}#/native`;
    iframe.style.cssText =
      "position:absolute;width:0;height:0;border:0;visibility:hidden;pointer-events:none;";

    const authData = {
      authToken: user.ACCESS_TOKEN,
      refreshToken: user.REFRESH_TOKEN,
      isMobileDummy: false,
      url: `${CY_PLUGINS_URL}#/unread`,
    };
    iframe.onload = () => {
      // NativeComponent stores the token then redirects the iframe to #/unread.
      // Retry a few times: onload can fire before the plugin's React tree has
      // attached its message listener. Extra posts on the #/unread page are
      // harmless (no listener there).
      [0, 250, 750].forEach((delay) => {
        setTimeout(() => {
          iframe.contentWindow?.postMessage(authData, "*");
        }, delay);
      });
    };

    document.body.appendChild(iframe);
    iframeRef.current = iframe;

    return () => {
      window.removeEventListener("message", onMessage);
      iframe.remove();
      iframeRef.current = null;
    };
  }, []);

  return (
    <ChatUnreadContext.Provider value={{ unreadCount }}>
      {children}
    </ChatUnreadContext.Provider>
  );
}
